import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { recalcLine, round2, rateToCode } from '../utils/lineMath';

const InvoiceForm = ({
  invoiceData,
  onDataChange,
  onSave,
  onReviewed,
  onReprocess,
  isLoading = false,
  validationErrors = {},
  confidenceScores = {}
}) => {
  const [formData, setFormData] = useState(invoiceData);
  const [comments, setComments] = useState('');
  const [vendorOptions, setVendorOptions] = useState([]);
  const vendorNameRef = useRef(null);
  const vendorSelectRef = useRef(null);
  const { currentOrgId } = useAuth();

  // VAT rate options
  const vatRateOptions = [
    { value: '0', label: '0% - Zero rated' },
    { value: '5', label: '5% - Reduced rate' },
    { value: '20', label: '20% - Standard rate' }
  ];

  // Currency options
  const currencyOptions = [
    { value: 'GBP', label: '£ - British Pound' },
    { value: 'EUR', label: '€ - Euro' }
  ];

  // Fetch vendors from database
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('id, display_name, legal_name, account_ref')
          .eq('active', true)
          .eq('org_id', currentOrgId)
          .order('display_name', { ascending: true });

        if (error) throw error;

        // Transform to dropdown options
        const options = (data || []).map(vendor => ({
          value: vendor.account_ref,
          label: vendor.display_name,
          description: vendor.legal_name || vendor.account_ref
        }));

        setVendorOptions(options);
      } catch (error) {
        console.error('Error fetching vendors:', error);
        setVendorOptions([]);
      }
    };

    if (currentOrgId) fetchVendors();
  }, [currentOrgId]);

  useEffect(() => {
    setFormData(invoiceData);
  }, [invoiceData]);

  useEffect(() => {
    if (validationErrors?.vendorName && vendorNameRef?.current) {
      vendorNameRef.current.focus({ preventScroll: true });
      vendorNameRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (validationErrors?.vendorId && vendorSelectRef?.current) {
      vendorSelectRef.current.focus({ preventScroll: true });
      vendorSelectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [validationErrors]);

  const handleFieldChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onDataChange?.(updatedData);
  };

  const round2 = (num) => {
    const n = Number(num);
    if (!Number.isFinite(n)) return 0;
    return Math.round((n + Number.EPSILON) * 100) / 100;
  };


  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return round2(n).toFixed(2);
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLineItems = [...(formData?.lineItems || [])];
    const updated = { ...updatedLineItems?.[index], [field]: value };
    updatedLineItems[index] = recalcLine(updated);

    const updatedData = { ...formData, lineItems: updatedLineItems };
    setFormData(updatedData);
    onDataChange?.(updatedData);
  };

  const addLineItem = () => {
    const newLineItem = {
      id: Date.now(),
      description: '',
      quantity: '1',
      unitPrice: '0.00',
      caseQuantity: '',
      unitSize: '',
      priceBasis: 'each',
      vatRate: '20',
      vatCode: 'S',
      netTotal: '0.00',
      vatAmount: '0.00',
      total: '0.00'
    };
    const updatedData = {
      ...formData,
      lineItems: [...(formData?.lineItems || []), recalcLine(newLineItem)]
    };
    setFormData(updatedData);
    onDataChange?.(updatedData);
  };

  const removeLineItem = (index) => {
    const updatedLineItems = formData?.lineItems?.filter((_, i) => i !== index);
    const updatedData = { ...formData, lineItems: updatedLineItems };
    setFormData(updatedData);
    onDataChange?.(updatedData);
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-success';
    if (score >= 0.6) return 'text-warning';
    return 'text-error';
  };

  const getConfidenceIndicator = (field) => {
    const score = confidenceScores?.[field];
    if (typeof score !== 'number') return null;
    
    return (
      <div className={`inline-flex items-center space-x-1 text-xs ${getConfidenceColor(score)}`}>
        <Icon name="Zap" size={12} />
        <span>{Math.round(score * 100)}%</span>
      </div>
    );
  };

  const calculateTotals = () => {
    const lineItems = formData?.lineItems || [];
    const subtotal = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item?.netTotal) || 0),
      0
    );
    const vatAmount = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item?.vatAmount) || 0),
      0
    );
    const total = round2(subtotal + vatAmount);
    return { subtotal: round2(subtotal), vatAmount: round2(vatAmount), total };
  };

  const { subtotal, vatAmount, total } = calculateTotals();

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="FileText" size={20} className="text-primary" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">Invoice Editor</h2>
            <p className="text-sm text-muted-foreground">
              {formData?.filename || 'invoice.pdf'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {getConfidenceIndicator('overall')}
        </div>
      </div>
      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Vendor Information */}
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Icon name="Building2" size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Vendor Information</h3>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Vendor Name"
              value={formData?.vendorName || ''}
              onChange={(e) => handleFieldChange('vendorName', e?.target?.value)}
              error={validationErrors?.vendorName}
              description={getConfidenceIndicator('vendorName')}
              required
              ref={vendorNameRef}
            />
            
            <Input
              label="Vendor Address"
              value={formData?.vendorAddress || ''}
              onChange={(e) => handleFieldChange('vendorAddress', e?.target?.value)}
              error={validationErrors?.vendorAddress}
              description={getConfidenceIndicator('vendorAddress')}
            />
            
            <Input
              label="VAT Registration Number"
              value={formData?.vendorVatNumber || ''}
              onChange={(e) => handleFieldChange('vendorVatNumber', e?.target?.value)}
              error={validationErrors?.vendorVatNumber}
              description={getConfidenceIndicator('vendorVatNumber')}
              placeholder="GB123456789"
            />
          </div>
        </section>

        {/* Invoice Information */}
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Icon name="Receipt" size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Invoice Details</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Invoice Number"
              value={formData?.invoiceNumber || ''}
              onChange={(e) => handleFieldChange('invoiceNumber', e?.target?.value)}
              error={validationErrors?.invoiceNumber}
              description={getConfidenceIndicator('invoiceNumber')}
              required
            />
            
            <Input
              label="Invoice Date"
              type="date"
              value={formData?.invoiceDate || ''}
              onChange={(e) => handleFieldChange('invoiceDate', e?.target?.value)}
              error={validationErrors?.invoiceDate}
              description={getConfidenceIndicator('invoiceDate')}
              required
            />
            
            <Input
              label="Due Date"
              type="date"
              value={formData?.dueDate || ''}
              onChange={(e) => handleFieldChange('dueDate', e?.target?.value)}
              error={validationErrors?.dueDate}
              description={getConfidenceIndicator('dueDate')}
            />
            
            <Select
              label="Currency"
              options={currencyOptions}
              value={formData?.currency || 'GBP'}
              onChange={(value) => handleFieldChange('currency', value)}
              error={validationErrors?.currency}
            />
          </div>
        </section>

        {/* Vendor Matching */}
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Vendor Matching</h3>
          </div>
          
          <Select
            label="Select Vendor"
            options={vendorOptions}
            value={formData?.vendorId || ''}
            onChange={(value) => handleFieldChange('vendorId', value)}
            error={validationErrors?.vendorId}
            searchable
            clearable
            placeholder="Search vendors..."
            description="Use fuzzy matching to find the correct vendor"
            required
            ref={vendorSelectRef}
          />
        </section>

        {/* Line Items */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="List" size={16} className="text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Line Items</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addLineItem}
              iconName="Plus"
              iconPosition="left"
            >
              Add Item
            </Button>
          </div>
          
          <div className="border border-border rounded-lg overflow-visible">
            <div className="bg-muted px-4 py-2">
              <div className="grid grid-cols-16 gap-2 text-xs font-medium text-muted-foreground">
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Quantity (cases)</div>
                <div className="col-span-2">Unit Price</div>
                <div className="col-span-1">Case Qty</div>
                <div className="col-span-2">Unit Size</div>
                <div className="col-span-2">VAT Rate</div>
                <div className="col-span-1">Net</div>
                <div className="col-span-1">Total (Gross)</div>
                <div className="col-span-1"></div>
              </div>
            </div>
            
            <div className="divide-y divide-border">
              {(formData?.lineItems || [])?.map((item, index) => (
                <div key={item?.id || index} className="p-4">
                  <div className="grid grid-cols-16 gap-2 items-start">
                    <div className="col-span-4">
                      <Input
                        placeholder="Item description"
                        value={item?.description || ''}
                        onChange={(e) => handleLineItemChange(index, 'description', e?.target?.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="1"
                        value={item?.quantity || ''}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e?.target?.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-span-2">
                      <div className="space-y-1">
                        {(() => {
                          const cq = parseFloat(item?.caseQuantity);
                          const hasCq = Number.isFinite(cq) && cq > 0;
                          const unitEach = parseFloat(item?.unitPrice) || 0; // stored per-each internally
                          const displayValue = item?.priceBasis === 'case'
                            ? (hasCq ? (unitEach * cq) : unitEach)
                            : unitEach;
                          const formattedValue = Number.isFinite(displayValue)
                            ? formatCurrency(displayValue)
                            : '';
                          return (
                            <Input
                              type="number"
                              placeholder="0.00"
                              value={formattedValue}
                              onChange={(e) => {
                                const raw = parseFloat(e?.target?.value);
                                if (!Number.isFinite(raw)) {
                                  handleLineItemChange(index, 'unitPrice', e?.target?.value);
                                  return;
                                }
                                // Convert back to per-each for storage
                                const perEach = (item?.priceBasis === 'case' && hasCq) ? (raw / cq) : raw;
                                handleLineItemChange(index, 'unitPrice', String(perEach));
                              }}
                              min="0"
                              step="0.01"
                            />
                          );
                        })()}
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground">
                            {item?.priceBasis === 'case' ? 'per case' : 'per each'}
                          </span>
                          {(() => {
                            const cq = parseFloat(item?.caseQuantity);
                            const hasCq = Number.isFinite(cq) && cq > 0;
                            const unitEach = parseFloat(item?.unitPrice) || 0; // stored per-each
                            if (item?.priceBasis === 'case' && hasCq && unitEach > 0) {
                              return (
                                <span className="text-[10px] text-muted-foreground">≈ £{(unitEach).toFixed(2)} each</span>
                              );
                            }
                            if (item?.priceBasis === 'each' && hasCq && unitEach > 0) {
                              return (
                                <span className="text-[10px] text-muted-foreground">≈ £{(unitEach * cq).toFixed(2)} per case</span>
                              );
                            }
                            return <span />;
                          })()}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const li = formData?.lineItems?.[index] || {};
                              const cq = parseFloat(li?.caseQuantity);
                              const hasCq = Number.isFinite(cq) && cq > 0 ? cq : 1;
                              const upEach = parseFloat(li?.unitPrice) || 0; // stored per-each
                              const next = { ...li };
                              if (li?.priceBasis === 'case') {
                                next.priceBasis = 'each';
                                // Keep per-each the same when toggling to each
                                next.unitPrice = String(upEach);
                              } else {
                                next.priceBasis = 'case';
                                // Keep per-each the same internally; display will switch to case
                                next.unitPrice = String(upEach);
                              }
                              const updated = [...(formData?.lineItems || [])];
                              updated[index] = recalcLine(next);
                              const updatedData = { ...formData, lineItems: updated };
                              setFormData(updatedData);
                              onDataChange?.(updatedData);
                            }}
                            title="Toggle price basis"
                          >
                            Toggle
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-1">
                      <Input
                        type="number"
                        placeholder="e.g. 6"
                        value={item?.caseQuantity || ''}
                        onChange={(e) => handleLineItemChange(index, 'caseQuantity', e?.target?.value)}
                        min="0"
                        step="1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        placeholder="e.g. 500g, 1L"
                        value={item?.unitSize || ''}
                        onChange={(e) => handleLineItemChange(index, 'unitSize', e?.target?.value)}
                      />
                    </div>
                    <div className="col-span-2">
                    <Select
                      options={vatRateOptions}
                      value={String(item?.vatRate ?? '0')}
                      onChange={(value) => handleLineItemChange(index, 'vatRate', value)}
                    />
                    </div>
                    <div className="col-span-1">
                      <div className="text-sm font-medium text-foreground py-2 leading-tight">
                        £{(parseFloat(item?.netTotal || 0) || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">Net</div>
                    </div>
                    <div className="col-span-1">
                      <div className="text-sm font-medium text-foreground py-2 leading-tight">
                        £{(parseFloat(item?.total || 0) || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        VAT £{(parseFloat(item?.vatAmount || 0) || 0).toFixed(2)}
                        {item?.vatRate !== undefined && (
                          <span> ({item?.vatCode || rateToCode(item?.vatRate)} {String(item?.vatRate)}%)</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        iconName="Trash2"
                        className="text-error hover:text-error"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Totals */}
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Icon name="Calculator" size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Totals</h3>
          </div>
          
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal:</span>
              <span className="font-medium">£{subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">VAT Amount:</span>
              <span className="font-medium">£{vatAmount?.toFixed(2)}</span>
            </div>
            <div className="border-t border-border pt-2">
              <div className="flex justify-between text-base font-semibold">
                <span>Total:</span>
                <span>£{total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Comments */}
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Comments</h3>
          </div>
          
          <textarea
            className="w-full h-20 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
            placeholder="Add comments or notes about this invoice..."
            value={comments}
            onChange={(e) => setComments(e?.target?.value)}
          />
        </section>
      </div>
      {/* Actions */}
      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between">
          {/* Mistral reprocessing not implemented yet
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={onReprocess}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={isLoading}
            >
              Reprocess with Mistral
            </Button>
          </div>
          */}
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={onSave}
              iconName="Save"
              iconPosition="left"
              disabled={isLoading}
            >
              Save Draft
            </Button>
            
            <Button
              variant="success"
              onClick={onReviewed}
              iconName="CheckCircle"
              iconPosition="left"
              disabled={isLoading}
            >
              Reviewed
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;
